import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from "react";
import { Skeleton } from "@mantine/core";
import type { BlockKey } from "./keys";
import type { GuideBlockProps } from "./types";

type BlockComponent = LazyExoticComponent<ComponentType<GuideBlockProps>>;

/**
 * Live blocks the portal can render inside a guide step, by key (#436).
 * Lazy, so a guide page only loads the blocks it uses. A key missing here
 * hides its step — see `memberSteps` in `lib/guides.ts`.
 */
const REGISTRY: Partial<Record<BlockKey, BlockComponent>> = {
  "floorplan.location": lazy(() => import("./floorplan-location-block")),
  "portal.link": lazy(() => import("./portal-link-block")),
  "meeting.find_room": lazy(() => import("./find-room-block")),
  "alarm.schedule": lazy(() => import("./alarm-schedule-block")),
  // Built-in guides only: a quiz step, checked and ticked by the backend.
  "guide.quiz": lazy(() => import("./quiz-block")),
  // Built-in guides only (#436 MR 5): the same cards as their portal pages.
  "wifi.personal_credential": lazy(() => import("./wifi-credential-block")),
  "wifi.network_info": lazy(() => import("./wifi-networks-block")),
  "wifi.change_password": lazy(() => import("./wifi-password-block")),
  "company.billing": lazy(() => import("./billing-block")),
  "company.opening_hours": lazy(() => import("./opening-hours-block")),
  "company.booking_approval": lazy(() => import("./booking-approval-block")),
  "colleagues.invite": lazy(() => import("./invite-colleagues-block")),
  "slack.install": lazy(() => import("./slack-block")),
  // #436 MR 7
  "door.pin": lazy(() => import("./door-pin-block")),
  "print.start": lazy(() => import("./print-start-block")),
  "network.add_device": lazy(() => import("./add-device-block")),
  // "Company network" guide
  "network.plan_capacity": lazy(() => import("./plan-capacity-block")),
  "network.device_list": lazy(() => import("./device-list-block")),
  // "Doorbell sound" guide
  "doorbell.chime": lazy(() => import("./doorbell-chime-block")),
  // "Answer visitor calls" guide
  "doorbell.call_settings": lazy(() => import("./call-settings-block")),
  "doorbell.call_alerts": lazy(() => import("./call-alerts-block")),
  "doorbell.ring_me": lazy(() => import("./ring-me-block")),
};

export function hasBlock(key: string): boolean {
  return Object.prototype.hasOwnProperty.call(REGISTRY, key);
}

/** Renders the block for `blockKey`, or nothing when the portal has no component for it. */
export function GuideBlock({ blockKey, ...props }: GuideBlockProps & { blockKey: string }) {
  const Block = hasBlock(blockKey) ? REGISTRY[blockKey as BlockKey] : undefined;
  if (!Block) return null;
  return (
    <Suspense fallback={<Skeleton height={48} radius="md" />}>
      <Block {...props} />
    </Suspense>
  );
}
